<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="closeModal"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"></div>
        
        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-auto overflow-hidden transform transition-all"
            @click.stop
          >
            <!-- Close button -->
            <button
              @click="closeModal"
              class="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-all hover:scale-110"
              aria-label="Close modal"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Header -->
            <div class="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-4">
              <h3 class="text-2xl font-bold text-white">Watch Our Platform Demo</h3>
              <p class="text-white/90 mt-1">See how Peachy Pro Studios streamlines your content workflow</p>
            </div>

            <!-- Video Container -->
            <div class="relative bg-black" style="padding-bottom: 56.25%;">
              <iframe
                v-if="isOpen"
                class="absolute inset-0 w-full h-full"
                :src="videoUrl"
                title="Peachy Pro Studios Demo"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
            </div>

            <!-- Footer -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div class="flex flex-col sm:flex-row gap-3 justify-between items-center">
                <p class="text-sm text-gray-600">Ready to transform your content workflow?</p>
                <div class="flex gap-3">
                  <button
                    @click="closeModal"
                    class="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Close
                  </button>
                  <NuxtLink
                    to="/request-demo"
                    class="px-6 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                    @click="closeModal"
                  >
                    Request Demo
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
const props = defineProps<{
  isOpen: boolean;
  youtubeId?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

// Default to a placeholder - replace with actual Peachy Pro Studios video ID
const defaultVideoId = 'dQw4w9WgXcQ'; // Replace this with your actual YouTube video ID
const videoId = computed(() => props.youtubeId || defaultVideoId);
const videoUrl = computed(() => `https://www.youtube.com/embed/${videoId.value}?autoplay=1&rel=0`);

const closeModal = () => {
  emit('close');
};

// Close on Escape key
onMounted(() => {
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && props.isOpen) {
      closeModal();
    }
  };
  window.addEventListener('keydown', handleEscape);
  
  onUnmounted(() => {
    window.removeEventListener('keydown', handleEscape);
  });
});

// Prevent body scroll when modal is open
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
